'use client';

/**
 * Next.js route-level error boundary. Catches render-time errors in any
 * route segment and reports them to PostHog as a `$exception` event so
 * frontend crashes appear alongside user behavior in the same dashboard.
 *
 * Placement: lives at `/app/error.js` (root-level). Applies to every route
 * EXCEPT the root-layout itself — for root-layout errors Next.js requires
 * a separate `global-error.js` (handled below).
 */
import { useEffect } from 'react';
import Link from 'next/link';
import posthog from 'posthog-js';

export default function RouteError({ error, reset }) {
  useEffect(() => {
    try {
      posthog.captureException(error, {
        // Tag the boundary so PostHog can filter route-level from
        // global-error crashes in dashboards.
        error_boundary: 'route',
      });
    } catch {
      // Analytics must never hide the underlying error from the user.
    }
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 bg-gum-gray-100 dark:bg-gum-gray-900 text-gum-black dark:text-gum-white">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-2xl font-black">Something went wrong</h1>
        <p className="text-sm text-gum-gray-500">The page hit an error while rendering. Reloading usually clears it.</p>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={reset}
          className="px-5 py-2.5 font-bold bg-gum-pink text-black rounded-lg hover:brightness-110 active:scale-[0.98] transition"
        >
          Try again
        </button>
        <Link
          href="/"
          className="px-5 py-2.5 font-bold border-2 border-gum-gray-300 dark:border-gum-gray-700 rounded-lg hover:border-gum-pink active:scale-[0.98] transition"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}
